import React, { useEffect, useMemo, useState } from 'react';
import { twistedStackAPI, stackAPI, userAPI } from '../services/api';
import { getDiceBearUserAvatar } from '../services/dicebear';
import { getAvatarStyle } from '../utils/avatar';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { STACK_COLORS, STACK_TEXT_COLORS, getColorIndexForLabel } from '../utils/stackColors';
import { calculateScore, getScoreText } from '../utils/scoring';
import { getPositionDisplayStyles, getPositionInputStyles } from '../utils/stack';
import { acknowledgeAction } from '../utils/confirmationDialog';
import CharacterCounter from '../utils/characterCounter';
import Comments from './Comments';

const SortableCard = ({ card }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: card.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  // Calculate dynamic font size inline to avoid import issues
  const getFontSize = (text, maxWidth = 260, baseSize = 18, minSize = 10) => {
    if (!text || text.length === 0) return baseSize;
    
    // Create a temporary canvas to measure text width accurately
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    
    // Try with base size first
    context.font = `${baseSize}px -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif`;
    const textWidth = context.getTextMetrics ? context.getTextMetrics(text).width : context.measureText(text).width;
    
    // If text fits at base size, no need to reduce
    if (textWidth <= maxWidth) {
      return baseSize;
    }
    
    // Binary search for the optimal font size
    let low = minSize;
    let high = baseSize;
    let optimalSize = minSize;
    
    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      context.font = `${mid}px -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif`;
      const width = context.getTextMetrics ? context.getTextMetrics(text).width : context.measureText(text).width;
      
      if (width <= maxWidth) {
        optimalSize = mid;
        low = mid + 1; // Try a larger size
      } else {
        high = mid - 1; // Try a smaller size
      }
    }
    
    return optimalSize;
  };
  const fontSize = getFontSize(card.label, 260, 18, 10);
  return (
    <div ref={setNodeRef} style={{ 
      padding: '12px', 
      borderRadius: '8px', 
      backgroundColor: STACK_COLORS[card.id - 1], 
      color: STACK_TEXT_COLORS[card.id - 1], 
      fontWeight: 800, 
      fontSize: `${fontSize}px`, 
      cursor: 'grab', 
      userSelect: 'none', 
      touchAction: 'none', 
      minHeight: '48px', // Consistent height for drag cards
      display: 'flex',
      alignItems: 'center',
      ...style 
    }} {...attributes} {...listeners}>
      {card.label}
    </div>
  );
};

const TwistedStackProfile = ({ twistedId, onBack, onClose }) => {
  const [twisted, setTwisted] = useState(null);
  const [baseStack, setBaseStack] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [order, setOrder] = useState([1, 2, 3, 4, 5, 6]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [hiding, setHiding] = useState(false);
  const [sandboxing, setSandboxing] = useState(false);
  const [showEditConfirm, setShowEditConfirm] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', positions: Array(6).fill('') });
  const [editing, setEditing] = useState(false);

  const refreshTwistedStack = async () => {
    try {
      const response = await twistedStackAPI.getTwistedStack(twistedId);
      setTwisted(response.data);
    } catch (err) {
      console.error('Failed to refresh twisted stack:', err);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [me, response] = await Promise.all([
          userAPI.getMe(),
          twistedStackAPI.getTwistedStack(`${twistedId}?mark_viewed=true`),
        ]);
        setCurrentUser(me.data);
        const t = response.data;
        setTwisted(t);
        // Load base stack for sandbox info if needed
        try {
          const s = await stackAPI.getStack(t.stack_id);
          setBaseStack(s.data);
        } catch (_) {}
        // Initialize order from current user's original positions if present
        const positions = t.positions || [];
        const idByName = new Map(positions.map((name, idx) => [name, idx + 1]));
        const myEntry = (t.users || []).find(u => u.user_id === me.data.id);
        if (myEntry && Array.isArray(myEntry.positions) && myEntry.positions.length === 6) {
          // Map user positions to IDs, ensuring unique values
          const initial = myEntry.positions.map((label, index) => {
            const mappedId = idByName.get(label);
            // If position not found in map, use a unique fallback based on index
            return mappedId !== undefined ? mappedId : (index + 1);
          });
          if (initial.length === 6) setOrder(initial);
        } else {
          setOrder([1, 2, 3, 4, 5, 6]);
        }
      } catch (err) {
        setError('Failed to load twisted stack');
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [twistedId]);

  const needsCompletion = useMemo(() => {
    if (!twisted || !currentUser) return false;
    const me = (twisted.users || []).find(u => u.user_id === currentUser.id);
    return !(me && me.is_answered);
  }, [twisted, currentUser]);

  const sortedCards = useMemo(() => {
    if (!twisted) return [];
    return order.map((posId) => {
      const position = (twisted.positions || [])[posId - 1];
      const label = typeof position === 'string' ? position : position?.name || '';
      return { id: posId, label };
    });
  }, [twisted, order]);

  const baseUserPositionsById = useMemo(() => {
    try {
      const mapping = new Map();
      const list = (baseStack && Array.isArray(baseStack.users)) ? baseStack.users : [];
      for (const u of list) {
        mapping.set(u.user_id, Array.isArray(u.positions) ? u.positions : []);
      }
      return mapping;
    } catch (_) {
      return new Map();
    }
  }, [baseStack]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setOrder((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleSubmit = async () => {
    // Prevent double-clicks by checking if already submitting
    if (submitting) return;
    
    try {
      setSubmitting(true);
      await twistedStackAPI.completeTwistedStack(twistedId, order);
      const refreshed = await twistedStackAPI.getTwistedStack(twistedId);
      setTwisted(refreshed.data);
    } catch (err) {
      setError('Failed to submit');
      console.error(err);
      setSubmitting(false);
    }
    // Note: Don't setSubmitting(false) on success to keep button disabled
  };

  const handleHide = async () => {
    try {
      setHiding(true);
      await twistedStackAPI.hideTwistedStack(twistedId);
      onBack();
    } catch (err) {
      setError('Failed to hide twisted stack');
      console.error(err);
    } finally {
      setHiding(false);
    }
  };

  const handleEditTwistedStack = async () => {
    if (!twisted) return;
    try {
      setEditing(true);
      await twistedStackAPI.editTwistedStack(twistedId, {
        name: editForm.name || null,
        positions: editForm.positions
      });
      setShowEditDialog(false);
      // Reload the twisted stack to get updated data
      const refreshed = await twistedStackAPI.getTwistedStack(twistedId);
      setTwisted(refreshed.data);
    } catch (err) {
      console.error('Failed to edit twisted stack:', err);
      if (err.response?.status === 403) {
        // Twisted stack can no longer be edited (time expired or submissions added)
        await acknowledgeAction(
          'Cannot Edit Twisted Stack',
          'This twisted stack can no longer be edited. It may have been created more than an hour ago or someone has already responded.'
        );
        setShowEditDialog(false);
        // Reload the twisted stack to get updated data and hide the edit button
        const refreshed = await twistedStackAPI.getTwistedStack(twistedId);
        setTwisted(refreshed.data);
      } else {
        // Other errors
        await acknowledgeAction(
          'Error',
          'Failed to edit twisted stack. Please try again.'
        );
      }
    } finally {
      setEditing(false);
    }
  };

  const handleEditClick = () => {
    // Pre-populate the edit form with current twisted stack data
    setEditForm({
      name: twisted.name || '',
      positions: Array.isArray(twisted.positions) ? [...twisted.positions] : Array(6).fill('')
    });
    setShowEditConfirm(true);
  };

  const toggleSandbox = async () => {
    try {
      if (!baseStack) return;
      setSandboxing(true);
      const next = !Boolean(baseStack.is_sandboxed);
      await stackAPI.setSandbox(baseStack.id, next);
      const refreshed = await stackAPI.getStack(baseStack.id);
      setBaseStack(refreshed.data);
    } catch (err) {
      setError('Failed to update sandbox state');
      console.error(err);
    } finally {
      setSandboxing(false);
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div style={{ color: 'red' }}>{error}</div>;
  if (!twisted) return null;

  const ownerAvatarUrl = getDiceBearUserAvatar(twisted.owner_username, twisted.owner_avatar_id ?? 1);
  const ownerId = typeof twisted.owner_id === 'number' ? twisted.owner_id : null;
  const canSandbox = !!(baseStack && baseStack.audience && baseStack.audience.group && (currentUser?.role === 'ADMIN' || (!baseStack.is_sandboxed && currentUser?.id === baseStack.owner_id)));

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <h2 style={{ 
          margin: 0, 
          paddingRight: '20px', 
          wordWrap: 'break-word', 
          overflowWrap: 'break-word', 
          wordBreak: 'break-all',
          hyphens: 'auto',
          lineHeight: '1.2'
        }}>{twisted.name || 'Twisted Stack'}</h2>
        <button onClick={onClose} className="close-btn">&times;</button>
      </div>

      {/* Twist description under title */}
      <div style={{ marginTop: '-6px', marginBottom: '12px' }}>
        <span style={{ color: 'var(--color-purple)', fontWeight: 800, fontStyle: 'italic' }}>{twisted.twist_description}</span>
      </div>

      {/* Twisted by header with profile link */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '6px 0 18px 0', color: 'var(--color-text)' }}>
        <span style={{ fontWeight: 600 }}>Twisted by:</span>
        <button
          type="button"
          title={twisted.owner_username}
          onClick={() => { if (ownerId) { try { window.dispatchEvent(new CustomEvent('openUser', { detail: { userId: ownerId } })); } catch (_) {} } }}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}
        >
          <img src={ownerAvatarUrl} alt={`${twisted.owner_username} avatar`} style={getAvatarStyle('medium')} />
          <span style={{ color: 'var(--color-primary)', textDecoration: 'underline' }}>{twisted.owner_username}</span>
        </button>
      </div>

      {/* Link to original stack */}
      <div style={{ margin: '-10px 0 18px 0', color: 'var(--color-text)' }}>
        <span style={{ fontWeight: 600 }}>Original stack:</span>{' '}
        <button
          type="button"
          onClick={() => { try { window.dispatchEvent(new CustomEvent('openStack', { detail: { id: twisted.stack_id, type: 'stack' } })); } catch (_) {} }}
          style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--color-primary)', textDecoration: 'underline', fontWeight: 600 }}
        >
          {twisted.name || 'Stack'}
        </button>
      </div>

      {needsCompletion ? (
        <div>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={sortedCards.map(card => card.id)} strategy={verticalListSortingStrategy}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '10px', margin: '20px 0' }}>
                {sortedCards.map((card) => (
                  <SortableCard key={card.id} card={card} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
          <button className="btn" style={{ backgroundColor: '#28a745' }} disabled={submitting} onClick={handleSubmit}>
            {submitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      ) : (
        <div>
          <h3>Responses</h3>
          <div style={{ color: 'var(--color-text-muted)', fontStyle: 'italic', fontWeight: 700, margin: '4px 0 10px 0' }}>
            Original rankings on the left, updated rankings on the right.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '12px' }}>
            {(twisted.users || []).filter(u => u.is_answered).map((u) => {
              const originalPositions = baseUserPositionsById.get(u.user_id) || [];
              const twistedPositions = u.positions || [];
              const rows = Array.from({ length: Math.max(originalPositions.length, twistedPositions.length, 6) }, (_, i) => i);
              // Calculate score using twisted positions vs the current viewer's twisted positions (if available)
              const me = (twisted.users || []).find(x => x.user_id === (currentUser?.id));
              const requestingPositions = (me && Array.isArray(me.positions) && me.positions.length === 6) ? me.positions : null;
              return (
                <div key={u.id} style={{ border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text)', borderRadius: '8px', padding: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                    <img src={getDiceBearUserAvatar(u.username, u.avatar_id)} alt={`${u.username} avatar`} style={getAvatarStyle('medium')} />
                    <div style={{ fontWeight: 700 }}>{u.username}</div>
                    {(() => {
                      if (!requestingPositions || u.user_id === currentUser?.id) return null;
                      const score = calculateScore(requestingPositions, twistedPositions);
                      const info = getScoreText(score, false);
                      return info ? (
                        <div style={{ marginLeft: 'auto', color: info.color, fontWeight: 600, fontSize: '14px' }}>{info.text}</div>
                      ) : null;
                    })()}
                  </div>
                  {u.stack_order_name && (
                    <div style={{ marginBottom: '8px', fontSize: '14px', color: 'var(--color-text-secondary)', fontStyle: 'italic', fontWeight: 'bold' }}>
                      Ranked by {u.stack_order_name}
                    </div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 10px' }}>
                    {rows.map((idx) => {
                      const leftLabel = originalPositions[idx] || '';
                      const rightLabel = twistedPositions[idx] || '';
                      const leftColorIdx = getColorIndexForLabel((twisted.positions || []), leftLabel, idx);
                      const rightColorIdx = getColorIndexForLabel((twisted.positions || []), rightLabel, idx);
                      return (
                        <React.Fragment key={`${u.id}-row-${idx}`}>
                          <div style={getPositionDisplayStyles(leftLabel, STACK_COLORS[leftColorIdx], STACK_TEXT_COLORS[leftColorIdx])}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span>{leftLabel}</span>
                              {(() => {
                                // Find the corresponding position object from twisted.positions to get the URL
                                const positionObj = twisted.positions?.find(p => p?.name === leftLabel);
                                return positionObj?.url ? (
                                  <span 
                                    style={{ cursor: 'pointer', fontSize: '16px', marginLeft: '8px' }}
                                    onClick={() => window.open(positionObj.url, '_blank')}
                                    title={`Open ${positionObj.url}`}
                                  >
                                    🔗
                                  </span>
                                ) : null;
                              })()}
                            </div>
                          </div>
                          <div style={getPositionDisplayStyles(rightLabel, STACK_COLORS[rightColorIdx], STACK_TEXT_COLORS[rightColorIdx])}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span>{rightLabel}</span>
                              {(() => {
                                // Find the corresponding position object from twisted.positions to get the URL
                                const positionObj = twisted.positions?.find(p => p?.name === rightLabel);
                                return positionObj?.url ? (
                                  <span 
                                    style={{ cursor: 'pointer', fontSize: '16px', marginLeft: '8px' }}
                                    onClick={() => window.open(positionObj.url, '_blank')}
                                    title={`Open ${positionObj.url}`}
                                  >
                                    🔗
                                  </span>
                                ) : null;
                              })()}
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Comments section - only show for completed twisted stacks */}
      {twisted.comments && (
        <Comments
          comments={twisted.comments}
          currentUser={currentUser}
          stackId={twistedId}
          isTwisted={true}
          onCommentUpdate={refreshTwistedStack}
          canSandbox={canSandbox}
        />
      )}

      {/* Actions: Back, Hide, Sandbox (if available) */}
      <div style={{ marginTop: '20px' }}>
        <h3>Actions</h3>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '10px' }}>
          <button onClick={onBack} className="btn" style={{ backgroundColor: 'var(--color-primary)', color: 'var(--text-on-primary)' }}>
            Back
          </button>
          {/* Show Edit button only if user is owner, no submissions, and within 1 hour */}
          {currentUser?.id === twisted?.owner_id && (!twisted?.users || twisted.users.length === 0) && (() => {
            const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
            const createdAt = new Date(twisted.created_at);
            return createdAt > oneHourAgo;
          })() && (
            <button onClick={handleEditClick} className="btn" style={{ backgroundColor: '#28a745', color: 'white' }}>
              Edit
            </button>
          )}
          <button onClick={handleHide} className="btn" style={{ backgroundColor: 'var(--color-warning)', color: 'var(--text-on-warning)' }} disabled={hiding}>
            {hiding ? 'Hiding...' : 'Hide'}
          </button>
          {canSandbox && (
            <button onClick={toggleSandbox} className="btn" style={{ backgroundColor: baseStack?.is_sandboxed ? '#6c757d' : '#fd7e14', color: 'white' }} disabled={sandboxing}>
              {sandboxing ? (baseStack?.is_sandboxed ? 'Unsandboxing...' : 'Sandboxing...') : (baseStack?.is_sandboxed ? 'Unsandbox' : 'Sandbox')}
            </button>
          )}
        </div>
      </div>

      {/* Edit Confirmation Dialog */}
      {showEditConfirm && (
        <div className="modal-overlay">
          <div className="modal-card">
            <h3 className="modal-title">Edit Stack?</h3>
            <p className="modal-text">You can only edit a stack within one hour of posting, and only if no one has responded.</p>
            <div className="modal-actions">
              <button onClick={() => setShowEditConfirm(false)} className="btn">
                Cancel
              </button>
              <button onClick={() => { setShowEditConfirm(false); setShowEditDialog(true); }} className="btn" style={{ backgroundColor: '#28a745', color: 'white' }}>
                Proceed
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Stack Dialog */}
      {showEditDialog && (
        <div className="modal-overlay">
          <div className="modal-card" style={{ maxWidth: '500px', width: '90%' }}>
            <h3 className="modal-title">Edit {twisted?.audience?.type === 'friend' ? 'Friend' : 'Group'} Stack</h3>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: 'var(--color-text)' }}>
                Stack Name (optional)
              </label>
              <input
                type="text"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                placeholder="Enter stack name"
                maxLength={32}
                style={{ width: '100%', padding: '8px', border: '1px solid var(--color-border)', borderRadius: '4px', fontSize: '14px' }}
              />
              <CharacterCounter current={editForm.name.length} max={48} />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: 'var(--color-text)' }}>
                Positions (6 required)
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '10px' }}>
                {editForm.positions.map((position, index) => (
                  <div key={index}>
                    <input
                      type="text"
                      value={position}
                      onChange={(e) => {
                        const newPositions = [...editForm.positions];
                        newPositions[index] = e.target.value;
                        setEditForm({ ...editForm, positions: newPositions });
                      }}
                      placeholder={`Position ${index + 1}`}
                      maxLength={32}
                      style={getPositionInputStyles(position, STACK_TEXT_COLORS[index], STACK_COLORS[index])}
                    />
                    <CharacterCounter current={position.length} max={48} />
                  </div>
                ))}
              </div>
            </div>

            <div className="modal-actions">
              <button onClick={() => setShowEditDialog(false)} className="btn" disabled={editing}>
                Cancel
              </button>
              <button 
                onClick={handleEditTwistedStack} 
                className="btn" 
                style={{ backgroundColor: '#28a745', color: 'white' }} 
                disabled={editing || editForm.positions.some(p => !p.trim())}
              >
                {editing ? 'Editing...' : 'Edit Stack'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TwistedStackProfile;


